import { DrawerBody, DrawerFooter, Text } from "@fluentui/react-components";

import OverlaySpinner from "../../../components/OverlaySpinner";
import LoadingButton from "../../../components/LoadingButton";

import FormNav from "./form/FormNav";
import BasicInfo from "./form/BasicInfo";
import DeviceInfo from "./form/DeviceInfo";
import ISOInfo from "./form/ISOInfo";
import StatusInfo from "./form/StatusInfo";
import DeviceStatsInfo from "./form/DeviceStatsInfo";
import WarrantyInfo from "./form/WarrantyInfo";

import useDeviceFormHook from "../hooks/useDeviceFormHook";
import { useDeviceDrawerStore } from "../store/DeviceDrawerStore";
import { useUtilitiesContext } from "../../../contexts/UtilitiesContext";
import { useDevicesContext } from "../context/DevicesContext";

import { usePostDeviceMutation } from "../api/Create";
import { usePutDeviceMutation } from "../api/Update";

import { Device } from "../data/Device";

export default function DeviceForm() {
  const { isEdit, data, close } = useDeviceDrawerStore();
  const {
    styles,
    labelState,
    serialNumberState,
    ownerState,
    statusState,
    locationState,
    typeState,
    vendorState,
    manufacturerState,
    modelState,
    availabilityState,
    integrityState,
    confidentialityState,
    cpuState,
    memoryState,
    diskState,
    priceState,
    purchaseDateState,
    isWarrantyState,
    laptopBagState,
    warrantyDateState,
    getLabelPrefix,
  } = useDeviceFormHook();
  const { showNotification } = useUtilitiesContext();
  const { refetchDevices } = useDevicesContext();

  const onError = (error: Error) => {
    showNotification({
      type: "error",
      title: "Error",
      message: error.message,
    });
  };

  const postDeviceMutation = usePostDeviceMutation((data: Device) => {
    showNotification({
      type: "success",
      title: "Created",
      message: (
        <Text>
          A request for approval to create device <b>{data.label}</b> has been
          sent. Please wait for approval.
        </Text>
      ),
    });
    close();
    refetchDevices();
  }, onError);

  const putDeviceMutation = usePutDeviceMutation(() => {
    showNotification({
      type: "success",
      title: "Updated",
      message: (
        <Text>
          A request for approval to update device <b>{data?.label}</b> has been
          sent. Please wait for approval.
        </Text>
      ),
    });
    close();
    refetchDevices();
  }, onError);

  const validateFields = () => {
    const prefix = getLabelPrefix(typeState.value);

    const rawLabelValue = labelState.value?.startsWith(prefix)
      ? labelState.value.slice(prefix.length)
      : labelState.value;

    const requiredFields = [
      { value: rawLabelValue && rawLabelValue.trim().length > 0, message: "Label is required" },
      {
        value: labelState.value && labelState.value.length <= 20,
        message: "Label must not exceed 20 characters.",
      },
      {
        value: !serialNumberState.value || serialNumberState.value.length < 50,
        message: "Serial number must not exceed 50 characters",
      },
      { value: ownerState.value > 0, message: "Owner is required" },

      { value: typeState.value > 0, message: "Type is required" },

      {
        value: availabilityState.value > 0,
        message: "Availability is required",
      },
      { value: integrityState.value > 0, message: "Integrity is required" },
      {
        value: confidentialityState.value > 0,
        message: "Confidentiality is required",
      },

      { value: statusState.value > 0, message: "Status is required" },

      {
        value:
          !isWarrantyState.value ||
          !warrantyDateState.value ||
          !purchaseDateState.value ||
          warrantyDateState.value > purchaseDateState.value,
        message: "Warranty date must be greater than Purchase date",
      },
    ];

    for (const field of requiredFields) {
      if (!field.value) {
        showNotification({
          type: "error",
          title: "Error",
          message: field.message,
        });
        return false;
      }
    }

    return true;
  };

  const onSubmit = () => {
    if (!validateFields()) return;

    const deviceData = {
      label: labelState.value ?? ``,
      serialNumber: serialNumberState.value,
      owner: ownerState.value,
      location: locationState.value,
      status: statusState.value,
      type: typeState.value,
      vendor: vendorState.value,
      manufacturer: manufacturerState.value,
      model: modelState.value,
      availability: availabilityState.value,
      integrity: integrityState.value,
      confidentiality: confidentialityState.value,
      cpu: cpuState.value,
      memory: memoryState.value,
      disk: diskState.value,
      price: priceState.value,
      purchaseDate: purchaseDateState.value,
      isWarranty: isWarrantyState.value ?? false,
      warrantyDate: warrantyDateState.value,
      laptopBag: laptopBagState.value ?? false,
    };

    if (isEdit && data) {
      putDeviceMutation.mutate({
        ...deviceData,
        id: data.id,
      });
    } else {
      postDeviceMutation.mutate(deviceData);
    }
  };

  const isLoading = isEdit
    ? putDeviceMutation.isPending
    : postDeviceMutation.isPending;
  const loadingLabel = isEdit ? "Updating..." : "Creating...";
  const buttonText = isEdit ? "Update" : "Create";

  return (
    <>
      <OverlaySpinner open={isLoading} label={loadingLabel} />

      <DrawerBody className={styles.drawerBody}>
        <BasicInfo
          styles={styles.inputWrapper}
          typeState={typeState}
          labelState={labelState}
          serialNumberState={serialNumberState}
          ownerState={ownerState}
          locationState={locationState}
          getLabelPrefix={getLabelPrefix}
        />
        <DeviceInfo
          styles={styles.inputWrapper}
          vendorState={vendorState}
          manufacturerState={manufacturerState}
          modelState={modelState}
          laptopBagState={laptopBagState}
        />
        <ISOInfo
          styles={styles.inputWrapper}
          availabilityState={availabilityState}
          integrityState={integrityState}
          confidentialityState={confidentialityState}
        />
        <StatusInfo styles={styles.inputWrapper} statusState={statusState} />
        <DeviceStatsInfo
          styles={styles.inputWrapper}
          cpuState={cpuState}
          memoryState={memoryState}
          diskState={diskState}
          priceState={priceState}
        />
        <WarrantyInfo
          styles={styles.inputWrapper}
          purchaseDateState={purchaseDateState}
          isWarrantyState={isWarrantyState}
          warrantyDateState={warrantyDateState}
        />
      </DrawerBody>

      <DrawerFooter className={styles.drawerFooter}>
        <FormNav totalSteps={6} />
        <LoadingButton
          text={buttonText}
          disabled={isLoading}
          loading={isLoading}
          onClick={onSubmit}
          appearance="primary"
        />
      </DrawerFooter>
    </>
  );
}
